import type React from 'react';
import { useState } from 'react';

import { Button, ButtonGroup, Heading, TextButton } from '@zillow/constellation-10';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import CancellationConfirmation from './OpenModalCancelSigning/CancellationConfirmation';
import CancellationPrompt from './OpenModalCancelSigning/CancellationPrompt';
import LeaseModal from '../../Containers/LeaseModal';
import { Lease } from '../../../models';
import type { LeaseActionProps } from '../../../models/Types';
import { useCancelSigningMutation } from '../../../utils/queries';

type Props = LeaseActionProps;

export function OpenModalCancelSigning({ history, terminateActiveAction, lease }: Props): JSX.Element {
  const [activeComponent, setActiveComponent] = useState(OpenModalCancelSigning.CancellationPrompt);
  const [cancellationReason, setCancellationReason] = useState('');
  const cancelSigningMutation = useCancelSigningMutation();

  function gotoDashboard(): void {
    terminateActiveAction();
    history.push(Lease.getDashboardPath());
  }

  function onOkayClick(): void {
    terminateActiveAction();
  }

  async function onCancelSigningClick(): Promise<void> {
    await cancelSigningMutation.mutateAsync({
      postData: {
        leaseArtifactId: lease.id as unknown as number,
        leaseArtifactType: LeaseArtifactType.LEASE,
        message: cancellationReason,
      },
    });

    setActiveComponent(OpenModalCancelSigning.CancellationConfirmation);
  }

  function onCancellationReasonChange(event: React.FormEvent<HTMLTextAreaElement>): void {
    setCancellationReason(event.currentTarget.value);
  }

  function renderBody(): React.ReactNode {
    const componentMap = {
      [OpenModalCancelSigning.CancellationPrompt]: (
        <CancellationPrompt
          key={OpenModalCancelSigning.CancellationPrompt}
          lease={lease}
          cancellationReason={cancellationReason}
          onCancellationReasonChange={onCancellationReasonChange}
        />
      ),
      [OpenModalCancelSigning.CancellationConfirmation]: (
        <CancellationConfirmation
          lease={lease}
          key={OpenModalCancelSigning.CancellationConfirmation}
          cancellationReason={cancellationReason}
        />
      ),
    };

    return <>{componentMap[activeComponent]}</>;
  }

  function onGotoDashboard() {
    gotoDashboard();
  }

  function onViewLease() {
    terminateActiveAction();
  }

  function handleClose() {
    if (!cancelSigningMutation.isPending) {
      terminateActiveAction();
    }
  }

  function renderFooter(): React.ReactNode {
    const isOnDashboard = window.location.pathname === Lease.getDashboardPath();

    const componentMap = {
      [OpenModalCancelSigning.CancellationPrompt]: (
        <ButtonGroup aria-label="cancel signing">
          <TextButton
            tone="brand"
            data-test="go-back"
            onClick={terminateActiveAction}
            disabled={cancelSigningMutation.isPending}
          >
            No
          </TextButton>
          <Button
            data-test="cancel-signing"
            disabled={cancellationReason === '' || cancelSigningMutation.isPending}
            tone="brand"
            emphasis="filled"
            loading={cancelSigningMutation.isPending}
            onClick={onCancelSigningClick}
          >
            Yes
          </Button>
        </ButtonGroup>
      ),
      [OpenModalCancelSigning.CancellationConfirmation]: isOnDashboard ? (
        <Button onClick={onOkayClick} tone="brand" emphasis="filled">
          OK
        </Button>
      ) : (
        <ButtonGroup aria-label="lease canceled options">
          <TextButton tone="brand" data-test="goto-dashboard-button" onClick={onGotoDashboard}>
            Go to dashboard
          </TextButton>
          <Button data-test="view-lease-button" tone="brand" emphasis="filled" onClick={onViewLease}>
            View lease
          </Button>
        </ButtonGroup>
      ),
    };

    return componentMap[activeComponent];
  }

  return (
    <LeaseModal
      header={
        <Heading level={1}>
          {activeComponent === OpenModalCancelSigning.CancellationPrompt ? 'Cancel signing?' : 'Lease signing canceled'}
        </Heading>
      }
      isOpen
      onClose={handleClose}
      body={renderBody()}
      footer={renderFooter()}
    />
  );
}

OpenModalCancelSigning.CancellationConfirmation = 'CancellationConfirmation';
OpenModalCancelSigning.CancellationPrompt = 'CancellationPrompt';

export default OpenModalCancelSigning;
